// ═══════════════════════════════════════════════════════════════════════════
// El pedido usual de un cliente
//
// SUSTITUYE AL BOTÓN DUPLICAR. El operador: "es una optimización gratuita que
// puede confundir. Es mejor eliminar duplicar. En todo caso, al pedir generar
// una nueva remisión debe decir algo como '¿Agregar pedido usual de este
// cliente?'".
//
// La diferencia no es cosmética:
//
//   Duplicar    partía de UN documento viejo, elegido a mano, desde la
//               pantalla del historial. Insinuaba que ese documento se estaba
//               tocando, y dejaba dos remisiones parecidas sin relación
//               declarada — fácil de confundir con un cobro doble.
//
//   Pedido usual  parte del PATRÓN de lo que ese cliente pide, y aparece en
//               el momento en que sirve: al empezar una remisión nueva. No
//               toca nada viejo.
//
// CÓMO SE CALCULA, y por qué así: por producto se toma la cantidad que MÁS SE
// REPITE en sus últimas remisiones, no el promedio. Un cliente que pide 12,
// 12, 12 y una vez 60 pide 12 — el promedio diría 24, que no es un pedido que
// haya hecho nunca. La moda siempre devuelve una cantidad real.
//
// Solo entran los productos que aparecen en al menos la MITAD de sus pedidos.
// Lo que compró una vez no es su pedido usual.
// ═══════════════════════════════════════════════════════════════════════════

/** Cuántas remisiones recientes se miran. Más allá, el patrón ya es viejo. */
const VENTANA = 6;

/**
 * @param invoices  todas las remisiones (se filtran aquí)
 * @param clientId  el cliente
 * @param products  catálogo actual: lo descontinuado no se sugiere
 * @returns null si no hay patrón, o
 *          { lineas: [{productId, qty}], basadoEn, esUltimo, etiqueta }
 */
export function pedidoUsual(invoices, clientId, products = []) {
  if (!clientId) return null;

  const enCatalogo = new Set(products.map(p => p.id));

  // Canceladas fuera: un pedido que se anuló no dice qué suele pedir.
  const suyas = (invoices || [])
    .filter(i => i.client?.id === clientId)
    .filter(i => !i.canceledAt && i.cfdiStatus !== "canceled")
    .filter(i => (i.lines || []).some(l => l.qty > 0))
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, VENTANA);

  if (!suyas.length) return null;

  // Con un solo pedido no hay "usual" que valga. Se ofrece igual, pero
  // llamándolo por su nombre: repetir el último. Mentirle al usuario sobre en
  // qué se basa la sugerencia es peor que no sugerir.
  const esUltimo = suyas.length === 1;

  const cuentas = new Map();   // productId → Map(qty → veces)
  const apariciones = new Map();

  for (const inv of suyas) {
    const vistos = new Set();
    for (const li of inv.lines || []) {
      const qty = Number(li.qty) || 0;
      if (qty <= 0) continue;                    // las cortesías van en 0
      if (!enCatalogo.has(li.productId)) continue;
      if (!cuentas.has(li.productId)) cuentas.set(li.productId, new Map());
      const m = cuentas.get(li.productId);
      m.set(qty, (m.get(qty) || 0) + 1);
      if (!vistos.has(li.productId)) {
        vistos.add(li.productId);
        apariciones.set(li.productId, (apariciones.get(li.productId) || 0) + 1);
      }
    }
  }

  const minimo = esUltimo ? 1 : Math.ceil(suyas.length / 2);

  const lineas = [];
  for (const [productId, porCantidad] of cuentas) {
    if ((apariciones.get(productId) || 0) < minimo) continue;

    // La cantidad más repetida. En empate gana la MAYOR: quedarse corto
    // obliga a corregir a mano, y es el error más fácil de no notar.
    let mejor = null, veces = 0;
    for (const [qty, n] of porCantidad) {
      if (n > veces || (n === veces && qty > mejor)) { mejor = qty; veces = n; }
    }
    lineas.push({ productId, qty: mejor });
  }

  if (!lineas.length) return null;

  return {
    lineas,
    basadoEn: suyas.length,
    esUltimo,
    etiqueta: esUltimo
      ? "Repetir su último pedido"
      : `Pedido usual (según sus últimas ${suyas.length} remisiones)`,
  };
}

/** Resumen legible para el aviso, sin obligar a abrir nada. */
export function describirPedido(sugerencia, products = []) {
  if (!sugerencia) return "";
  const nombre = (id) => products.find(p => p.id === id)?.name || id;
  return sugerencia.lineas
    .map(l => `${l.qty} × ${nombre(l.productId)}`)
    .join(" · ");
}
